import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    onSnapshot,
    Timestamp
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import Swal from 'sweetalert2';

function Pumpdetails() {
    const [formData, setFormData] = useState({
        pumpName: "",
        mobile: "",
        email: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        gstName: "",
        gstNo: "",
        expiryDate: "",
        status: "Active",
        annualFee: "",
        role: "Admin"
    });

    const [userId, setUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [exists, setExists] = useState(false);

    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
    });

    // 1. Auth Listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserId(user.uid);
            } else {
                setUserId(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // 2. Fetch User/Pump Data
    useEffect(() => {
        if (!userId) return;

        const userDocRef = doc(db, "Users", userId);

        // Using onSnapshot for real-time updates
        const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists()) {
                setFormData(docSnap.data());
                setExists(true);
            } else {
                setExists(false);
                // Pre-fill email from auth if profile doesn't exist
                setFormData(prev => ({ ...prev, email: auth.currentUser.email }));
            }
            setFetching(false);
        });

        return () => unsubscribe();
    }, [userId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // 3. Add or Update Details
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Safety Check: Ensure we have a logged-in User ID
        if (!userId) {
            return Toast.fire({ icon: 'error', title: 'User session not found!' });
        }

        setLoading(true);
        try {
            // 1. Reference the document in the "users" collection using the UID
            // CRITICAL: Ensure the collection name matches your Firestore exactly (usually lowercase "users")
            const userDocRef = doc(db, "Users", userId);

            // 2. Prepare the data payload
            const dataToSave = {
                ...formData,
                pumpName: (formData.pumpName || "").toUpperCase(), // Handle potential null/undefined
                updatedAt: Timestamp.now(),
                userId: userId // Maintain the link to the Auth UID
            };

            // 3. The "Super Save": setDoc with merge: true
            // This will CREATE the doc if it doesn't exist, or UPDATE it if it does.
            // It will NOT overwrite fields not present in formData (like 'role' or 'password')
            await setDoc(userDocRef, dataToSave, { merge: true });

            Toast.fire({
                icon: 'success',
                title: 'Profile Saved Successfully',
                text: 'Your pump details are now up to date.'
            });

        } catch (error) {
            console.error("Firestore Save Error:", error);

            // Provide a more descriptive error to the user
            Toast.fire({
                icon: 'error',
                title: 'Update Failed',
                text: error.message
            });
        } finally {
            setLoading(false);
        }
    };

    // 4. "Delete" logic (Resetting profile)
    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Reset Details?',
            text: "This will clear your pump profile information!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, Reset'
        });

        if (result.isConfirmed) {
            // Usually we don't delete the user doc, we just reset fields
            setFormData({
                pumpName: "", mobile: "", email: auth.currentUser.email,
                address: "", city: "", state: "", pincode: "",
                gstName: "", gstNo: "", expiryDate: "", status: "Inactive", annualFee: ""
            });
            Toast.fire({ icon: 'info', title: 'Fields Cleared locally. Save to apply.' });
        }
    };

    if (fetching) return <div className="content-wrapper p-5">Loading Profile...</div>;

    return (
        <div className="content-wrapper">
            <section className="content-header">
                <div className="container-fluid">
                    <h1>Pump Business Details</h1>
                </div>
            </section>

            <section className="content">
                <div className="container-fluid">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            {/* Basic Info */}
                            <div className="col-md-6">
                                <div className="card card-outline card-primary shadow">
                                    <div className="card-header">
                                        <h3 className="card-title">General Information</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="form-group">
                                            <label>Pump/Bunk Name</label>
                                            <input name="pumpName" value={formData.pumpName} onChange={handleChange} className="form-control" required />
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Mobile</label>
                                                    <input name="mobile" value={formData.mobile} onChange={handleChange} className="form-control" required />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Email</label>
                                                    <input name="email" value={formData.email} onChange={handleChange} className="form-control" readOnly />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Address</label>
                                            <textarea name="address" value={formData.address} onChange={handleChange} className="form-control" rows="2" />
                                        </div>
                                        <div className="row">
                                            <div className="col-md-4"><label>City</label><input name="city" value={formData.city} onChange={handleChange} className="form-control" /></div>
                                            <div className="col-md-4"><label>State</label><input name="state" value={formData.state} onChange={handleChange} className="form-control" /></div>
                                            <div className="col-md-4"><label>Pincode</label><input name="pincode" value={formData.pincode} onChange={handleChange} className="form-control" /></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Statutory & Fees */}
                            <div className="col-md-6">
                                <div className="card card-outline card-success shadow">
                                    <div className="card-header">
                                        <h3 className="card-title">Tax & Subscription</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="form-group">
                                            <label>GST Registered Name</label>
                                            <input name="gstName" value={formData.gstName} onChange={handleChange} className="form-control" />
                                        </div>
                                        <div className="form-group">
                                            <label>GST Number</label>
                                            <input name="gstNo" value={formData.gstNo} onChange={handleChange} className="form-control" />
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Expiry Date</label>
                                                    <input type="date" name="expiryDate" value={formData.expiryDate} onChange={handleChange} className="form-control" />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Annual Fee (₹)</label>
                                                    <input type="number" name="annualFee" readOnly value={formData.annualFee} onChange={handleChange} className="form-control" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Status</label>
                                            <select name="status" readOnly value={formData.status} onChange={handleChange} className="form-control">
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                                <option value="Suspended">Suspended</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="card-footer">
                                        <button type="submit" disabled={loading} className="btn btn-success mr-2">
                                            {loading ? 'Saving...' : 'Save All Changes'}
                                        </button>
                                        <button type="button" onClick={handleDelete} className="btn btn-outline-danger">
                                            Reset Profile
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </section>
        </div>
    );
}

export default Pumpdetails;